import { useState, useEffect, useRef } from "react";
import { StarRatingButton, StarRatingGroup } from "../index";
import { RATINGS_WITHOUT_REVIEWS_TEXT } from "../ratingTypes";
import useRatingsFeatureRequirements from "../../../hooks/ratingsFeatureRequirements/useRatingsFeatureRequirements";
import { useRuntimeConstants } from "../../../context/runtimeConstants/RuntimeConstantsContext";
import useSyndicateReviewsExperiment from "../../ratingsAndReviews/useSyndicateReviewsExperiment";
import getReviews from "../../../api/ratingsApi";
import styles from "./Rating.scss";

const SYNDICATED_FILTER = "isSyndicated:eq:false";

interface RatingsData {
  averageOverallRating?: number;
  averageOverallStarRating?: number;
  totalReviewCount?: number;
  displayRatingsSection?: boolean;
}

interface DisplayStats {
  averageOverallStarRating: number;
  averageOverallRating: number;
  totalReviewCount: number;
}

interface RatingsProps extends DisplayStats {
  language: string;
}

interface RenderRatingsArgs {
  displayRatingsSection: boolean | undefined;
  ratingsProps: RatingsProps;
  productLink: string | undefined;
}

function renderRatings({
  displayRatingsSection,
  ratingsProps,
  productLink,
}: RenderRatingsArgs) {
  const ratingGroup = (
    <StarRatingGroup {...ratingsProps} type={RATINGS_WITHOUT_REVIEWS_TEXT} />
  );

  if (displayRatingsSection) {
    return <StarRatingButton {...ratingsProps} />;
  }

  if (productLink) {
    return (
      <a href={productLink} className={styles.productLink}>
        {ratingGroup}
      </a>
    );
  }

  return ratingGroup;
}

interface RatingProps {
  ratingsFromProduct?: RatingsData;
  productLink?: string;
  isInStock?: boolean;
  productId?: number;
}

export default function Rating({
  ratingsFromProduct,
  productLink,
  isInStock,
  productId,
}: RatingProps) {
  const { webContext, ratings, reviewNumberThreshold, features } =
    useRuntimeConstants() as {
      webContext: { browseLanguage: string };
      ratings: RatingsData;
      reviewNumberThreshold: number;
      features?: { ratings?: boolean };
    };

  const initialRatingsEligible = useRef<boolean>(
    ratingsFromProduct
      ? (ratingsFromProduct.totalReviewCount ?? 0) >= reviewNumberThreshold
      : !!ratings?.displayRatingsSection,
  );
  const { isFiltered } = useSyndicateReviewsExperiment(
    initialRatingsEligible.current,
  );

  const isMixAndMatch = !!ratingsFromProduct;

  const baseRatings = ratingsFromProduct ?? ratings;
  const [hiddenAfterFilter, setHiddenAfterFilter] = useState(false);
  const [displayStats, setDisplayStats] = useState<DisplayStats>({
    averageOverallStarRating: baseRatings?.averageOverallStarRating ?? 0,
    averageOverallRating: baseRatings?.averageOverallRating ?? 0,
    totalReviewCount: baseRatings?.totalReviewCount ?? 0,
  });

  const ratingsFeatureRequirementsAchieved = useRatingsFeatureRequirements(
    baseRatings,
    isInStock,
  );

  useEffect(() => {
    const meetsReviewThreshold =
      Boolean(ratingsFromProduct?.averageOverallStarRating) &&
      parseFloat(String(ratingsFromProduct?.totalReviewCount ?? 0)) >=
        reviewNumberThreshold &&
      isInStock === true;

    if (
      !isFiltered ||
      !features?.ratings ||
      !isMixAndMatch ||
      !meetsReviewThreshold ||
      productId == null ||
      !ratingsFeatureRequirementsAchieved
    ) {
      return;
    }

    let cancelled = false;

    getReviews({
      offset: 0,
      filter: [SYNDICATED_FILTER],
      productId,
    })
      .then(
        ({
          ratingsStatistics,
          totalResults,
        }: {
          ratingsStatistics: RatingsData | null;
          totalResults?: number;
        }) => {
          if (cancelled) return;

          const nativeReviewCount =
            ratingsStatistics?.totalReviewCount ?? totalResults ?? null;

          if (
            nativeReviewCount != null &&
            nativeReviewCount < reviewNumberThreshold
          ) {
            setHiddenAfterFilter(true);
          } else if (ratingsStatistics) {
            setDisplayStats({
              averageOverallStarRating:
                ratingsStatistics.averageOverallStarRating ?? 0,
              averageOverallRating: ratingsStatistics.averageOverallRating ?? 0,
              totalReviewCount: ratingsStatistics.totalReviewCount ?? 0,
            });
          }
        },
      )
      .catch(() => {
        // Preserve SSR values on failure
      });

    return () => {
      cancelled = true;
    };
  }, [
    isFiltered,
    features?.ratings,
    isMixAndMatch,
    ratingsFromProduct?.averageOverallStarRating,
    ratingsFromProduct?.totalReviewCount,
    reviewNumberThreshold,
    isInStock,
    productId,
    ratingsFeatureRequirementsAchieved,
  ]);

  if (!ratingsFeatureRequirementsAchieved || hiddenAfterFilter) {
    return null;
  }

  const { displayRatingsSection } = baseRatings;

  const activeStats = isMixAndMatch
    ? displayStats
    : {
        averageOverallStarRating: baseRatings?.averageOverallStarRating ?? 0,
        averageOverallRating: baseRatings?.averageOverallRating ?? 0,
        totalReviewCount: baseRatings?.totalReviewCount ?? 0,
      };

  const ratingsProps: RatingsProps = {
    averageOverallStarRating: activeStats.averageOverallStarRating,
    averageOverallRating: activeStats.averageOverallRating,
    totalReviewCount: activeStats.totalReviewCount,
    language: webContext.browseLanguage,
  };

  return (
    <>
      {renderRatings({
        displayRatingsSection,
        ratingsProps,
        productLink,
      })}
    </>
  );
}
